// Export helpers: per-packet CSV, whole-run / whole-payload JSON (schema:1), and
// PNG snapshots of a chart canvas. The app is served by the Go binary (not a
// sandboxed frame), so ordinary anchor-download works here.

import type { RunResult } from '@/engine/types';
import type { SharePayload } from './share';

/** Trigger a browser download of a Blob under the given filename. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Download a text file. */
export function downloadText(text: string, filename: string, mime = 'text/plain'): void {
  downloadBlob(new Blob([text], { type: `${mime};charset=utf-8` }), filename);
}

/** Download any JSON-serialisable value pretty-printed. */
export function downloadJSON(value: unknown, filename: string): void {
  downloadText(JSON.stringify(value, null, 2), filename, 'application/json');
}

const CSV_COLUMNS = [
  'seq',
  'class',
  'sentUs',
  'rttMs',
  'serverSeq',
  'outOfOrder',
] as const;

function csvCell(value: string | number | boolean | null): string {
  if (value === null) return '';
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Build a per-packet CSV string for one run. */
export function runToCSV(run: RunResult): string {
  const lines: string[] = [CSV_COLUMNS.join(',')];
  for (const p of run.packets) {
    lines.push(
      [
        csvCell(p.seq),
        csvCell(p.cls),
        csvCell(p.sentUs),
        csvCell(p.rttMs),
        csvCell(p.serverSeq),
        csvCell(p.outOfOrder),
      ].join(','),
    );
  }
  return lines.join('\n');
}

/** A stable, human-readable file stem for a run. */
export function runFileStem(run: RunResult): string {
  const dt = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `plm-${run.tier}-${run.nodeId}-${dt}`.replace(/[^A-Za-z0-9._-]/g, '_');
}

/** Download one run's per-packet data as CSV. */
export function exportPacketsCSV(run: RunResult): void {
  downloadText(runToCSV(run), `${runFileStem(run)}.csv`, 'text/csv');
}

/** Download one run (metrics + packets) as JSON. */
export function exportRunJSON(run: RunResult): void {
  downloadJSON({ schema: 1, kind: 'runResult', run }, `${runFileStem(run)}.json`);
}

/** Download a whole share payload (run set or sweep) as JSON. */
export function exportPayloadJSON(payload: SharePayload): void {
  const dt = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  downloadJSON(payload, `plm-${payload.kind}-${dt}.json`);
}

/**
 * Export a chart canvas to PNG. Charts render onto a <canvas>; the caller passes
 * the DOM node (or a container to search within). Falls back silently when no
 * canvas is found.
 */
export function exportCanvasPNG(el: HTMLElement | null, filename: string): void {
  if (!el) return;
  const canvas = el instanceof HTMLCanvasElement ? el : el.querySelector('canvas');
  if (!canvas) return;
  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, filename);
  }, 'image/png');
}
